import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { FaMoon, FaSun, FaSyncAlt } from 'react-icons/fa';

const API_URL = process.env.REACT_APP_API_URL || 'https://api.mogym.ir';

const palette = {
  bgDark: '#0e1015',
  cardDark: '#1a1d2e',
  borderDark: '#273043',
  textDark: '#ffffff',
  subDark: '#cbd5e1',
  textLight: '#0f172a',
  subLight: '#475569',
  primary: '#2563eb',
  error: '#ef4444',
};

const formatRemaining = (seconds) => {
  if (seconds === null || seconds === undefined) return null;
  const s = Math.floor(Number(seconds));
  if (!Number.isFinite(s) || s <= 0) return null;

  const h = String(Math.floor(s / 3600)).padStart(2, '0');
  const m = String(Math.floor((s % 3600) / 60)).padStart(2, '0');
  const sec = String(s % 60).padStart(2, '0');

  return `${h}:${m}:${sec}`;
};

export default function History() {
  const navigate = useNavigate();

  const [dark, setDark] = useState(true);
  const [history, setHistory] = useState([]);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [error, setError] = useState('');

  const colors = useMemo(
    () => ({
      bg: dark ? palette.bgDark : '#ffffff',
      card: dark ? palette.cardDark : '#ffffff',
      border: dark ? palette.borderDark : '#e5e7eb',
      text: dark ? palette.textDark : palette.textLight,
      sub: dark ? palette.subDark : palette.subLight,
      chipBg: dark ? '#141827' : '#f8fafc',
    }),
    [dark]
  );

  useEffect(() => {
    try {
      const stored = localStorage.getItem('theme.dark');
      if (stored !== null) {
        setDark(stored === 'true');
      }
    } catch {}
  }, []);

  const toggleTheme = () => {
    setDark((prev) => {
      const next = !prev;
      try {
        localStorage.setItem('theme.dark', next ? 'true' : 'false');
      } catch {}
      return next;
    });
  };

  const loadHistory = useCallback(async () => {
    try {
      setError('');
      setLoading(true);

      const token = localStorage.getItem('token');
      if (!token) {
        setError('ابتدا وارد حساب کاربری شوید.');
        return;
      }

      const res = await fetch(`${API_URL}/api/plan/history`, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });

      if (res.status === 401) {
        localStorage.removeItem('token');
        navigate('/login');
        return;
      }

      const data = await res.json();

      if (!res.ok) {
        throw new Error(data?.message || data?.Message || 'خطا در دریافت تاریخچه');
      }

      setHistory(data?.result || data?.Result || []);
    } catch (e) {
      console.error('history error', e);
      setError('خطا در دریافت اطلاعات تاریخچه');
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  }, [navigate]);

  useEffect(() => {
    loadHistory();
  }, [loadHistory]);

  const onRefresh = async () => {
    setRefreshing(true);
    await loadHistory();
  };

  const statusColor = (status) => {
    switch (status) {
      case 'Registered':
        return '#e5e7eb';
      case 'Paid':
        return '#f97316';
      case 'Canceled':
        return '#ef4444';
      case 'PaymentPending':
        return '#eab308';
      case 'Created':
        return '#22c55e';
      case 'TrainerReview':
        return '#3b82f6';

      case 'PendingRevision':
        return '#a855f7';
      case 'RevisionInReview':
        return '#6366f1';
      case 'Revised':
        return '#10b981';
      case 'RevisionRejected':
        return '#ef4444';

      default:
        return colors.sub;
    }
  };

  const statusText = (status) => {
    switch (status) {
      case 'Registered':
        return 'ثبت شده';
      case 'Paid':
        return 'پرداخت شده';
      case 'Canceled':
        return 'لغو شده';
      case 'PaymentPending':
        return 'در حال پرداخت';
      case 'Created':
        return 'ارسال شده';
      case 'TrainerReview':
        return 'در حال ساخت';

      case 'PendingRevision':
        return 'در انتظار اصلاح';
      case 'RevisionInReview':
        return 'در حال بررسی اصلاح';
      case 'Revised':
        return 'اصلاح شد';
      case 'RevisionRejected':
        return 'رد شد';

      default:
        return 'نامشخص';
    }
  };

  const formatDate = (dt) => {
    if (!dt) return '-';
    const d = new Date(dt);
    if (Number.isNaN(d.getTime())) return '-';

    const y = d.getFullYear();
    const m = String(d.getMonth() + 1).padStart(2, '0');
    const day = String(d.getDate()).padStart(2, '0');

    return `${y}/${m}/${day}`;
  };

  const handleDetails = (planId) => {
    if (!planId) return;
    localStorage.setItem('planId', String(planId));
    navigate('/active-plan');
  };

  const handleFeedback = (planId) => {
    navigate('/feedback', { state: { planId } });
  };

  const handleRevision = (planId) => {
    if (!planId) return;
    navigate(`/revision-request/${planId}`);
  };

  const handleRevisionDetails = (planId) => {
    if (!planId) return;
    navigate(`/revision-details/${planId}`);
  };

  return (
    <div className={dark ? 'dark' : ''}>
      <div
        className="min-h-screen px-4 py-8 font-vazir"
        style={{
          backgroundColor: colors.bg,
          color: colors.text,
          direction: 'rtl',
        }}
      >
        <div className="max-w-4xl mx-auto">
          {/* Header */}
          <div className="flex items-center justify-between mb-6">
            <h1 className="text-2xl font-bold">تاریخچه برنامه‌ها</h1>

            <div className="flex items-center gap-2">
              <button
                onClick={onRefresh}
                disabled={loading || refreshing}
                className="px-3 py-2 rounded-full border transition disabled:opacity-60"
                style={{
                  backgroundColor: colors.chipBg,
                  borderColor: colors.border,
                }}
                title="بروزرسانی"
              >
                <FaSyncAlt
                  className={refreshing ? 'animate-spin' : ''}
                  color={dark ? '#cbd5e1' : '#334155'}
                  size={14}
                />
              </button>

              <button
                onClick={toggleTheme}
                className="p-3 rounded-full border transition"
                style={{
                  backgroundColor: dark ? '#141827' : '#e5e7eb',
                  borderColor: colors.border,
                }}
                aria-label="toggle theme"
              >
                {dark ? (
                  <FaSun className="text-yellow-400" size={16} />
                ) : (
                  <FaMoon className="text-slate-900" size={16} />
                )}
              </button>
            </div>
          </div>

          {loading ? (
            <div className="min-h-[40vh] flex flex-col items-center justify-center">
              <div
                className="w-12 h-12 border-4 border-t-transparent rounded-full animate-spin"
                style={{
                  borderColor: palette.primary,
                  borderTopColor: 'transparent',
                }}
              />
              <p className="mt-3 text-sm" style={{ color: colors.sub }}>
                در حال دریافت اطلاعات...
              </p>
            </div>
          ) : (
            <>
              {error ? (
                <p
                  className="text-center mb-4 text-sm"
                  style={{ color: palette.error }}
                >
                  {error}
                </p>
              ) : null}

              {history.length === 0 ? (
                <p
                  className="text-center text-sm"
                  style={{ color: colors.sub }}
                >
                  هیچ برنامه‌ای یافت نشد.
                </p>
              ) : null}

              <div className="space-y-4">
                {history.map((item, index) => {
                  const revision = item?.revision;
                  const status = item?.Status || item?.status;
                  const planId = item?.PlanId || item?.planId;
                  const planTitle = item?.PlanTitle || item?.planTitle;
                  const planType = item?.PlanType || item?.planType;
                  const createdAt = item?.CreatedAt || item?.createdAt;
                  const trackingCode = item?.TrackingCode || item?.trackingCode;

                  const canShowRevisionButton = status === 'Created';
                  const revisionEnabled = !!revision?.isEligible;

                  const showRevisionDetailsButton =
                    status === 'PendingRevision' ||
                    status === 'RevisionInReview' ||
                    status === 'Revised' ||
                    status === 'RevisionRejected';

                  return (
                    <div
                      key={index}
                      className="border rounded-2xl p-4"
                      style={{
                        borderColor: colors.border,
                        backgroundColor: colors.card,
                      }}
                    >
                      <div className="flex items-center justify-between mb-3 gap-3">
                        <span
                          className="text-sm font-medium"
                          style={{ color: statusColor(status) }}
                        >
                          {statusText(status)}
                        </span>

                        <span
                          className="font-bold text-right flex-1"
                          style={{ color: colors.text }}
                        >
                          {planTitle}
                        </span>
                      </div>

                      <p
                        className="text-sm mb-2 text-right"
                        style={{ color: colors.sub }}
                      >
                        نوع برنامه: {planType || '-'}
                      </p>

                      <p
                        className="text-sm mb-2 text-right"
                        style={{ color: colors.sub }}
                      >
                        تاریخ ایجاد: {formatDate(createdAt)}
                      </p>

                      <p
                        className="text-sm mb-3 text-right"
                        style={{ color: colors.sub }}
                      >
                        کد رهگیری: {trackingCode || '-'}
                      </p>

                      <div className="flex flex-wrap gap-2 justify-end">
                        <button
                          onClick={() => handleDetails(planId)}
                          className="px-4 py-1.5 rounded-full text-sm font-medium"
                          style={{
                            backgroundColor: '#2563eb',
                            color: '#fff',
                          }}
                        >
                          مشاهده جزئیات
                        </button>

                        {status === 'Created' && (
                          <button
                            onClick={() => handleFeedback(planId)}
                            className="px-4 py-1.5 rounded-full text-sm font-medium"
                            style={{
                              backgroundColor: '#16a34a',
                              color: '#fff',
                            }}
                          >
                            ثبت بازخورد
                          </button>
                        )}

                        {canShowRevisionButton && (
                          <button
                            disabled={!revisionEnabled}
                            onClick={() => handleRevision(planId)}
                            className="px-4 py-1.5 rounded-full text-sm font-medium transition disabled:cursor-not-allowed"
                            style={{
                              backgroundColor: revisionEnabled ? '#f59e0b' : '#334155',
                              color: '#fff',
                              opacity: revisionEnabled ? 1 : 0.6,
                            }}
                          >
                            درخواست اصلاح
                            {revisionEnabled && revision?.remainingSeconds
                              ? `  ${formatRemaining(revision.remainingSeconds) || ''} مانده`
                              : ''}
                          </button>
                        )}

                        {showRevisionDetailsButton && (
                          <button
                            onClick={() => handleRevisionDetails(planId)}
                            className="px-4 py-1.5 rounded-full text-sm font-medium"
                            style={{
                              backgroundColor: '#7c3aed',
                              color: '#fff',
                            }}
                          >
                            مشاهده درخواست اصلاح
                          </button>
                        )}
                      </div>

                      {canShowRevisionButton && (
                        <div className="mt-3">
                          {!revisionEnabled ? (
                            <p
                              className="text-[11px] text-right leading-6"
                              style={{ color: colors.sub }}
                            >
                              {revision?.reasonMessage || 'امکان درخواست اصلاح وجود ندارد.'}
                            </p>
                          ) : (
                            <p
                              className="text-[11px] text-right leading-6"
                              style={{ color: colors.sub }}
                            >
                              تا ۲۴ ساعت پس از ایجاد برنامه، فقط یک‌بار می‌توانید درخواست اصلاح ثبت کنید.
                            </p>
                          )}

                          {revision?.isDownloadedPlan ? (
                            <p
                              className="mt-1 text-[11px] text-right leading-6"
                              style={{ color: colors.sub }}
                            >
                              توجه: به دلیل دانلود کامل برنامه، بررسی ممکن است کمی زمان‌برتر باشد.
                            </p>
                          ) : null}
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
}